import { lowerCase } from 'lodash-es';
import { useEffect } from 'react';

import { PreAnnotationModal } from '@/component';
import { useRequestFramePreAnnotationMutation } from '@/hook';
import { AiAnno, AiAssist, useFlag, useShortcut } from '@/package';
import {
  useLabelingBoxStore,
  useTaskFrameStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import type { BoxWithLabel, Modeling } from '@/type';

import ToolbarGroup from './ToolbarGroup';
import ToolbarMenu from './ToolbarMenu';

const AIToolbarGroup = () => {
  const { namespace, register, unregister } = useShortcut();
  const { value: visible, setFalse: hide, setTrue: show } = useFlag(false);
  const { currentFrame, isFrameDataLoading, selectedTask } =
    useTaskFrameStore();
  const { permission } = useToolPermissionStore();
  const { mutateAsync: runPreAnnotation } =
    useRequestFramePreAnnotationMutation();
  const { setBoxList } = useLabelingBoxStore();
  const { toolMap, findByName, select } = useToolMenuStore();
  const preAnnotationTool = findByName('preAnnotation')!;
  const aiAssistTool = findByName('aiAssist')!;
  const isLabelUpdatable = permission.labeling.updatable;
  const { isWorkState } = useTaskFrameStore();
  const isNotStarted = isWorkState('Prework');
  const trackSession =
    currentFrame?.usableTrackSession ?? currentFrame?.curTrackSession;

  const onConfirm = (modelingID: Modeling['id'], useTrackSession: boolean) => {
    if (!selectedTask || !currentFrame || isFrameDataLoading)
      return Promise.reject(
        new Error(
          '[AIToolbarGroup] `selectedTask` or `currentFrame` or `isFrameDataLoading` is null',
        ),
      );

    return runPreAnnotation({
      modelingID,
      pFrameID: currentFrame.frameSeq,
      pTaskID: selectedTask.id,
      useSession: useTrackSession,
    }).then(res => {
      setBoxList(
        res.data.resultData.workData.map(
          v =>
            ({
              ...v,
              label: lowerCase(v.label),
            }) as BoxWithLabel,
        ),
      );

      hide();
    });
  };

  useEffect(() => {
    preAnnotationTool.isSelected && show();
  }, [toolMap]);

  useEffect(() => {
    register(preAnnotationTool.command!, () => select(preAnnotationTool.id));
    register(aiAssistTool.command!, () => select(aiAssistTool.id));

    return () => {
      unregister(preAnnotationTool.command!);
      unregister(aiAssistTool.command!);
    };
  }, [namespace]);

  return (
    <>
      <ToolbarGroup className="rounded-sm" visibleDivider>
        <ToolbarMenu
          style={
            isNotStarted
              ? {
                  cursor: 'not-allowed',
                  background: '#E5E5E5',
                  color: '#999999',
                }
              : {
                  background:
                    'linear-gradient(121deg, #B384FF 10.35%, #00B4D8 43.96%, #648DFF 83.16%)',
                  backgroundSize: '300% 300%',
                  animation: 'tool-ai_anno-gradient 3s ease-in-out infinite',
                }
          }
          className="size-9"
          toolMenuItem={preAnnotationTool}
          onClick={() => select(preAnnotationTool.id)}
          disabled={!isLabelUpdatable}
        >
          <AiAnno className="size-8" />
        </ToolbarMenu>

        <ToolbarMenu
          className="size-9"
          toolMenuItem={aiAssistTool}
          onClick={() => select(aiAssistTool.id)}
          disabled={!isLabelUpdatable}
        >
          <AiAssist className="size-8" />
        </ToolbarMenu>
      </ToolbarGroup>

      {selectedTask && (
        <PreAnnotationModal
          usableTrackSession={trackSession}
          task={selectedTask}
          visible={visible}
          enabledTrackSession
          setVisible={v => (v ? show() : hide())}
          onConfirm={onConfirm}
        />
      )}
    </>
  );
};

export default AIToolbarGroup;
